import type { Loan } from 'app/types/loan';
import * as Calendar from 'expo-calendar/legacy';
import type { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { getCalendars } from 'expo-localization';
import { PermissionStatus } from 'expo';
import { Event, track } from './analytics';
import type { User } from 'app/types/user';
import { isLender } from './user';
import { APP_SCHEME } from 'app/../app.config.json';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { addDays, subDays, differenceInCalendarDays } from 'app/lib/date';
import { recordError } from 'app/lib/monitoring';

const CALENDAR_NAME = 'partage-club';
const CALENDAR_OWNER = 'Partage Club';
const CALENDAR_TITLE = `${CALENDAR_OWNER} Rendez-vous`;
const TIMEZONE = getCalendars()[0].timeZone ?? 'America/Montreal';

async function createCalendar(): Promise<string> {
  const source =
    Platform.OS === 'ios'
      ? { name: CALENDAR_OWNER, type: Calendar.SourceType.LOCAL }
      : { isLocalAccount: true, name: CALENDAR_OWNER, type: 'LOCAL' };

  return Calendar.createCalendarAsync({
    title: CALENDAR_TITLE,
    color: '#C0EBDF',
    entityType: Calendar.EntityTypes.EVENT,
    source,
    name: CALENDAR_NAME,
    ownerAccount: CALENDAR_OWNER,
    accessLevel: Calendar.CalendarAccessLevel.OWNER,
    ...(Platform.OS === 'android' ? { isVisible: true, isSynced: true, timeZone: TIMEZONE } : {}),
  });
}

async function makeCalendarVisible(calendar: Calendar.Calendar): Promise<string> {
  if (Platform.OS === 'android' && (!calendar.isVisible || !calendar.isSynced)) {
    await Calendar.updateCalendarAsync(calendar.id, { isVisible: true, isSynced: true });
  }
  return calendar.id;
}

async function getOrCreateCalendarId(): Promise<string | null> {
  try {
    const calendars = await Calendar.getCalendarsAsync(Calendar.EntityTypes.EVENT);
    const partageClubCalendar = calendars.find(
      (calendar) => calendar.title === CALENDAR_TITLE && calendar.allowsModifications,
    );
    if (partageClubCalendar) {
      return await makeCalendarVisible(partageClubCalendar);
    }
    return await createCalendar();
  } catch (error) {
    recordError(error);
    try {
      const calendars = await Calendar.getCalendarsAsync(Calendar.EntityTypes.EVENT);
      const writableCalendar = calendars.find((calendar) => calendar.allowsModifications);
      return writableCalendar?.id ?? null;
    } catch (fallbackError) {
      recordError(fallbackError);
      return null;
    }
  }
}

async function createEvent(calendarId: string, date: string, title: string, notes?: string) {
  const startDate = new Date(date);
  const event = {
    title,
    startDate,
    // Calendar APIs model all-day events as [start, end), so the end must be the following day.
    endDate: addDays(startDate, 1),
    timeZone: TIMEZONE,
    allDay: true,
    notes,
  };
  const eventId = await Calendar.createEventAsync(calendarId, event);
  return eventId;
}

export type AddLoanToCalendarResult = 'success' | 'permission-denied' | 'error' | 'no-loan';

const pendingLoanAdditions = new Map<Loan['id'], Promise<AddLoanToCalendarResult>>();
const pendingMilestoneAdditions = new Map<string, Promise<AddLoanToCalendarResult>>();

function getLoanCalendarEventDetails(t: ReturnType<typeof useTranslation>['t'], userId: User['id'], loan: Loan) {
  const isLenderUser = isLender(userId, loan.listing.userId, loan.listing.type);

  const neighbourName = isLenderUser ? loan.borrower?.profile?.firstName : loan.lender?.profile?.firstName;

  const isService = loan.listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(loan.listing);
  const isSell = loan.listing.transactionMode === TransactionMode.sell;

  const startTitleSuffix = isSell
    ? isLenderUser
      ? 'sellItem'
      : 'buyItem'
    : isService
      ? isLenderUser
        ? 'giveService'
        : 'receiveService'
      : isDonation
        ? isLenderUser
          ? 'donateItem'
          : 'receiveDonation'
        : isLenderUser
          ? 'lendObject.start'
          : 'borrowObject.start';

  return {
    end:
      isService || isDonation || isSell
        ? null
        : {
            date: loan.end,
            title: t(`addToCalendar.event.${isLenderUser ? 'lendObject.end' : 'borrowObject.end'}`, {
              name: neighbourName,
              item: loan.listing.item.name,
            }),
          },
    notes: `${APP_SCHEME}://main/management`,
    start: {
      date: loan.start,
      title: t(`addToCalendar.event.${startTitleSuffix}`, {
        name: neighbourName,
        item: loan.listing.item.name,
      }),
    },
  };
}

const addLoanToCalendarOnce = async (
  t: ReturnType<typeof useTranslation>['t'],
  userId: User['id'],
  loan?: Loan,
): Promise<AddLoanToCalendarResult> => {
  if (!loan) {
    return 'no-loan';
  }

  if (!loan.listing?.item) {
    return 'no-loan';
  }

  try {
    const { status } = await Calendar.requestCalendarPermissionsAsync();
    track(Event.CalendarPermissionStatus, { status });
    if (status !== PermissionStatus.GRANTED) {
      return 'permission-denied';
    }

    const calendarId = await getOrCreateCalendarId();
    if (!calendarId) {
      track(Event.LoanAddedToCalendar, { added: false });
      return 'error';
    }

    const existingEvents = await Calendar.getEventsAsync(
      [calendarId],
      subDays(new Date(loan.start), 1),
      addDays(new Date(loan.end), 1),
    );

    const eventDetails = getLoanCalendarEventDetails(t, userId, loan);

    const eventAlreadyExists = (title: string, when: string | Date) =>
      existingEvents.find(
        (event) => event.title === title && differenceInCalendarDays(new Date(when), new Date(event.startDate)) === 0,
      );

    if (!eventAlreadyExists(eventDetails.start.title, eventDetails.start.date)) {
      await createEvent(calendarId, eventDetails.start.date, eventDetails.start.title, eventDetails.notes);
    }
    if (eventDetails.end && !eventAlreadyExists(eventDetails.end.title, eventDetails.end.date)) {
      await createEvent(calendarId, eventDetails.end.date, eventDetails.end.title, eventDetails.notes);
    }

    track(Event.LoanAddedToCalendar, { added: true });
    return 'success';
  } catch (error: unknown) {
    recordError(error);
    track(Event.LoanAddedToCalendar, { added: false });
    return 'error';
  }
};

export const addLoanToCalendar = (
  t: ReturnType<typeof useTranslation>['t'],
  userId: User['id'],
  loan?: Loan,
): Promise<AddLoanToCalendarResult> => {
  if (!loan) {
    return Promise.resolve('no-loan');
  }

  const pendingAddition = pendingLoanAdditions.get(loan.id);
  if (pendingAddition) {
    return pendingAddition;
  }

  const addition = addLoanToCalendarOnce(t, userId, loan).finally(() => pendingLoanAdditions.delete(loan.id));
  pendingLoanAdditions.set(loan.id, addition);
  return addition;
};

export type LoanCalendarMilestone = 'end' | 'start';

const addLoanMilestoneToCalendarOnce = async (
  t: ReturnType<typeof useTranslation>['t'],
  userId: User['id'],
  loan: Loan,
  milestone: LoanCalendarMilestone,
): Promise<AddLoanToCalendarResult> => {
  try {
    const { status } = await Calendar.requestCalendarPermissionsAsync();
    track(Event.CalendarPermissionStatus, { status });
    if (status !== PermissionStatus.GRANTED) return 'permission-denied';

    const calendarId = await getOrCreateCalendarId();
    if (!calendarId) {
      track(Event.LoanAddedToCalendar, { added: false });
      return 'error';
    }

    const loanEventDetails = getLoanCalendarEventDetails(t, userId, loan);
    const eventDetails = loanEventDetails[milestone];
    if (!eventDetails) return 'no-loan';

    const existingEvents = await Calendar.getEventsAsync(
      [calendarId],
      subDays(new Date(eventDetails.date), 1),
      addDays(new Date(eventDetails.date), 1),
    );
    const eventAlreadyExists = existingEvents.some(
      (event) =>
        event.title === eventDetails.title &&
        differenceInCalendarDays(new Date(eventDetails.date), new Date(event.startDate)) === 0,
    );

    if (!eventAlreadyExists) {
      await createEvent(calendarId, eventDetails.date, eventDetails.title, loanEventDetails.notes);
    }

    track(Event.LoanAddedToCalendar, { added: true });
    return 'success';
  } catch (error: unknown) {
    recordError(error);
    track(Event.LoanAddedToCalendar, { added: false });
    return 'error';
  }
};

export const addLoanMilestoneToCalendar = (
  t: ReturnType<typeof useTranslation>['t'],
  userId: User['id'],
  loan: Loan,
  milestone: LoanCalendarMilestone,
): Promise<AddLoanToCalendarResult> => {
  const key = `${loan.id}:${milestone}`;
  const pendingAddition = pendingMilestoneAdditions.get(key);
  if (pendingAddition) return pendingAddition;

  const addition = addLoanMilestoneToCalendarOnce(t, userId, loan, milestone).finally(() =>
    pendingMilestoneAdditions.delete(key),
  );
  pendingMilestoneAdditions.set(key, addition);
  return addition;
};
